/**
 * Customer API Contract Schemas
 *
 * Defines Zod schemas for customer-related API requests and responses.
 * Used for validation in both backend (NestJS) and frontend (React forms).
 */

import { z } from 'zod';

// ============================================
// ENUMS
// ============================================

export const LedgerEntryTypeEnum = z.enum(['CREDIT', 'DEBIT', 'FOREGONE', 'DISCOUNT']);
export type LedgerEntryType = z.infer<typeof LedgerEntryTypeEnum>;

// ============================================
// LEDGER ENTRY SCHEMAS
// ============================================

/**
 * Individual ledger entry representing a credit/debit transaction
 */
export const LedgerEntrySchema = z.object({
  id: z.string().uuid(),
  customerId: z.string().uuid(),
  vendorId: z.string().uuid(),
  amount: z.number().positive('Amount must be positive'),
  type: LedgerEntryTypeEnum,
  bookingId: z.string().uuid().nullable().optional(),
  description: z.string().max(500).nullable().optional(),
  createdAt: z.date(),
});
export type LedgerEntry = z.infer<typeof LedgerEntrySchema>;

/**
 * API response containing ledger entries and calculated balance
 */
export const CustomerLedgerResponseSchema = z.object({
  entries: z.array(LedgerEntrySchema),
  totalOwed: z.number(),
  totalDiscountsGiven: z.number(),
  totalForegone: z.number(),
});
export type CustomerLedgerResponse = z.infer<typeof CustomerLedgerResponseSchema>;

/**
 * Request to forego (write off) a credit amount
 */
export const ForegoAmountRequestSchema = z.object({
  amount: z.number().positive('Amount must be positive'),
  note: z.string().max(500).optional(),
});
export type ForegoAmountRequest = z.infer<typeof ForegoAmountRequestSchema>;

/** Zod schema for top debtor entry */
export const TopDebtorSchema = z.object({
  customerId: z.string(),
  customerName: z.string(),
  phone: z.string(),
  pendingAmount: z.number(),
  lastBookingDate: z.string().datetime(),
});

/** Zod schema for vendor credit summary response */
export const VendorCreditSummarySchema = z.object({
  totalPendingAmount: z.number(),
  totalPendingCustomers: z.number(),
  topDebtors: z.array(TopDebtorSchema),
  totalGrossPendingAmount: z.number().optional(),
  totalForegoneAmount: z.number().optional(),
});

export type TopDebtorDto = z.infer<typeof TopDebtorSchema>;
export type VendorCreditSummaryDto = z.infer<typeof VendorCreditSummarySchema>;
